// store/slices/syncSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RatingValue } from "../../common/types/enums";

// Типы статусов (числовые enum без присвоения)
export enum SyncActionType {
  FAVORITE,
  RATING,
  VIEW,
  SERVICE_OTP,
}

export enum SyncStatus {
  PENDING,
  SYNCING,
  FAILED,
  SYNCED,
}

export interface PendingAction {
  type: SyncActionType;
  itemId: number;
  userId: number;
  data?: {
    rating?: RatingValue;
    comment?: string;
    files?: string[];
    viewedAt?: Date;
    code?: string;
  };
  timestamp: number;
  localId: string;
  retryCount: number;
  status: SyncStatus;
}

export interface SyncState {
  pendingActions: PendingAction[];
  isSyncing: boolean;
  lastSyncTime?: number;
  error?: string;
}

const initialState: SyncState = {
  pendingActions: [],
  isSyncing: false,
  lastSyncTime: undefined,
  error: undefined,
};

export const syncSlice = createSlice({
  name: "sync",
  initialState,
  reducers: {
    // Добавить действие в очередь
    addPendingAction: (
      state,
      action: PayloadAction<{
        type: SyncActionType;
        itemId: number;
        userId: number;
        data?: {
          rating?: RatingValue;
          comment?: string;
          files?: string[];
          viewedAt?: Date;
          code?: string;
        };
      }>,
    ) => {
      const { type, itemId, userId, data } = action.payload;

      state.pendingActions.push({
        type,
        itemId,
        userId,
        data,
        timestamp: Date.now(),
        localId: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        retryCount: 0,
        status: SyncStatus.PENDING,
      });
    },

    // Удалить действие из очереди
    removePendingAction: (state, action: PayloadAction<string>) => {
      const localIdToRemove = action.payload;
      state.pendingActions = state.pendingActions.filter(
        (pendingAction) => pendingAction.localId !== localIdToRemove,
      );
    },

    // Обновить статус действия
    updateActionStatus: (
      state,
      action: PayloadAction<{
        localId: string;
        status: SyncStatus;
        error?: string;
      }>,
    ) => {
      const { localId, status, error } = action.payload;
      const actionIndex = state.pendingActions.findIndex(
        (pendingAction) => pendingAction.localId === localId,
      );

      if (actionIndex !== -1) {
        state.pendingActions[actionIndex].status = status;

        if (status === SyncStatus.FAILED) {
          state.pendingActions[actionIndex].retryCount += 1;
          if (error) {
            state.error = error;
          }
        }
      }
    },

    // Начать синхронизацию
    startSync: (state) => {
      state.isSyncing = true;
      state.error = undefined;

      // Помечаем все pending как syncing
      state.pendingActions.forEach((pendingAction) => {
        if (pendingAction.status === SyncStatus.PENDING) {
          pendingAction.status = SyncStatus.SYNCING;
        }
      });
    },

    // Завершить синхронизацию
    finishSync: (
      state,
      action: PayloadAction<{
        success: boolean;
        error?: string;
      }>,
    ) => {
      const { success, error } = action.payload;

      state.isSyncing = false;
      state.lastSyncTime = Date.now();

      if (success) {
        state.error = undefined;

        // Помечаем syncing как synced
        state.pendingActions.forEach((pendingAction) => {
          if (pendingAction.status === SyncStatus.SYNCING) {
            pendingAction.status = SyncStatus.SYNCED;
          }
        });
      } else {
        state.error = error;

        // Помечаем syncing как failed
        state.pendingActions.forEach((pendingAction) => {
          if (pendingAction.status === SyncStatus.SYNCING) {
            pendingAction.status = SyncStatus.FAILED;
            pendingAction.retryCount += 1;
          }
        });
      }
    },

    // Увеличить счетчик попыток для конкретного действия
    incrementRetryCount: (state, action: PayloadAction<string>) => {
      const localId = action.payload;
      const actionIndex = state.pendingActions.findIndex(
        (pendingAction) => pendingAction.localId === localId,
      );

      if (actionIndex !== -1) {
        state.pendingActions[actionIndex].retryCount += 1;

        if (state.pendingActions[actionIndex].retryCount > 3) {
          state.pendingActions[actionIndex].status = SyncStatus.FAILED;
        }
      }
    },

    // Очистить синхронизированные действия
    clearSyncedActions: (state) => {
      state.pendingActions = state.pendingActions.filter(
        (pendingAction) => pendingAction.status !== SyncStatus.SYNCED,
      );
    },

    // Очистить все действия
    clearAllActions: (state) => {
      state.pendingActions = [];
      state.isSyncing = false;
      state.error = undefined;
    },

    // Восстановить из хранилища
    restorePendingActions: (state, action: PayloadAction<PendingAction[]>) => {
      state.pendingActions = action.payload;
    },
  },
});

// Экспорт действий
export const {
  addPendingAction,
  removePendingAction,
  updateActionStatus,
  startSync,
  finishSync,
  incrementRetryCount,
  clearSyncedActions,
  clearAllActions,
  restorePendingActions,
} = syncSlice.actions;

// Селекторы
export const selectPendingActions = (state: { sync: SyncState }) =>
  state.sync.pendingActions;

export const selectPendingActionsByType =
  (type: SyncActionType) => (state: { sync: SyncState }) =>
    state.sync.pendingActions.filter(
      (pendingAction) => pendingAction.type === type,
    );

export const selectIsSyncing = (state: { sync: SyncState }) =>
  state.sync.isSyncing;

export const selectSyncError = (state: { sync: SyncState }) => state.sync.error;

export const selectHasPendingActions = (state: { sync: SyncState }) =>
  state.sync.pendingActions.length > 0;

export const selectFailedActions = (state: { sync: SyncState }) =>
  state.sync.pendingActions.filter(
    (pendingAction) =>
      pendingAction.status === SyncStatus.FAILED &&
      pendingAction.retryCount > 3,
  );

export default syncSlice.reducer;
